// Chat-Modul – Mapper (Backend -> vue-advanced-chat)

import { useSupplierStore } from "@/stores/suppliers";
import type {
    ApiMessage,
    ApiRoom,
    ChatMessage,
    ChatRoom,
    ChatFile,
} from "@/types/chat";

function getSupplierInfo(supplierId: string) {
    const supplierStore = useSupplierStore();
    const supplier = supplierStore.allSuppliers.find(
        (supplier) => supplier.id === supplierId,
    );
    if (supplier) {
        const logo = supplier.imageLink?.startsWith("/")
            ? `https://app.octopusorder.com${supplier.imageLink}`
            : supplier.imageLink
              ? supplier.imageLink
              : `assets/icon/${supplier.iconType || "deliverer"}.svg`;
        const name = supplier.name;
        return { logo, name };
    }
}

function formatDate(d: Date): string {
    return d.toLocaleDateString("de-DE", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

function formatTime(d: Date): string {
    return d.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
    });
}

function mimeToType(mime: string): string {
    if (mime === "application/pdf") return "pdf";
    if (mime === "image/png") return "png";
    if (mime === "image/jpeg") return "jpeg";
    return mime.split("/").pop() ?? "file";
}

function mapAttachments(msg: ApiMessage): ChatFile[] | undefined {
    if (!msg.attachments?.length) return undefined;

    return msg.attachments.map((a) => {
        const dot = a.name.lastIndexOf(".");
        // `vue-advanced-chat` expects name and extension as SEPARATE fields
        const baseName = dot > 0 ? a.name.slice(0, dot) : a.name;
        const extension =
            dot > 0 ? a.name.slice(dot + 1).toLowerCase() : "unknown";
        const isImage = a.mimeType.startsWith("image/");

        return {
            name: baseName,
            extension,
            type: a.mimeType, // full MIME type, matches the `vue-advanced-chat` own convention
            size: a.size,
            url: a.url,
            preview: isImage ? a.url : undefined,
        };
    });
}

export function mapApiMessage(
    m: ApiMessage,
    currentLocationId: string,
    supplierId: string
): ChatMessage {
    const d = new Date(m.createdAt);
    return {
        _id: m.id,
        content: m.body,
        senderId: m.author.type === 'user' ? currentLocationId : supplierId,
        username: m.author.type,
        date: formatDate(d),
        timestamp: formatTime(d),
        saved: true,
        distributed: m.deliveryStatus === 'sent' || m.deliveryStatus === 'delivered',
        // seen: /* m.senderId === currentLocationId ? true :*/ !!m.readAt,
        seen: m.isRead,
        files: mapAttachments(m),
    };
}

export function mapApiRoom(r: ApiRoom): ChatRoom {
    const storeSupplierInfo = getSupplierInfo(r.supplier.id); // API sent only partial data (mongoDB only)
    return {
        roomId: r.roomId,
        roomName: r.supplier.name || storeSupplierInfo?.name || "No Name",
        avatar: storeSupplierInfo?.logo,
        unreadCount: r.unreadCount,
        locationId: r.locationId,
        supplierId: r.supplier.id,
        users: [
            {
                _id: r.supplier.id,
                username: r.supplier.name || String(storeSupplierInfo?.name),
                avatar: storeSupplierInfo?.logo,
            },
        ],
        lastMessage: r.lastMessage
            ? {
                  content: r.lastMessage.body,
                  // senderId: r.lastMessage.senderId,
                  senderId: r.lastMessage.author.type === 'user' ? r.locationId : r.supplier.id,
                  timestamp: formatTime(new Date(r.lastMessage.createdAt)),
              }
            : undefined,
    };
}
